import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';

interface LayoutProps {
  children: React.ReactNode;
}

// Check if we're in staging environment
const isStaging = import.meta.env.VITE_APP_ENV === 'staging';

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation('common');
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [showNavLabels, setShowNavLabels] = useState(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem('showNavLabels');
    return saved !== null ? saved === 'true' : true;
  });
  const navMenuRef = useRef<HTMLDivElement>(null);
  const navMenuDesktopRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  // Save label preference
  const toggleNavLabels = () => {
    const newValue = !showNavLabels;
    setShowNavLabels(newValue);
    localStorage.setItem('showNavLabels', String(newValue));
  };

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
      // Check both desktop and mobile nav refs
      const clickedInNavMenu = navMenuRef.current?.contains(event.target as Node);
      const clickedInNavMenuDesktop = navMenuDesktopRef.current?.contains(event.target as Node);
      if (!clickedInNavMenu && !clickedInNavMenuDesktop) {
        setIsNavMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Check if a nav link (or its submodules) is active
  const isActive = (path: string, submodules?: { path: string }[]) => {
    if (path === '/') return location.pathname === '/';
    // Check main path
    if (location.pathname.startsWith(path)) return true;
    // Check submodule paths
    if (submodules?.some(sub =>
      sub.path === '/' ? location.pathname === '/' : location.pathname.startsWith(sub.path)
    )) return true;
    return false;
  };

  // Navigation structure with submodules
  interface NavLink {
    path: string;
    labelKey: string;
    icon: React.ComponentType<{ className?: string }>;
    submodules?: { path: string; labelKey: string; icon: React.ComponentType<{ className?: string }> }[];
  }

  const navLinks: NavLink[] = [
    { path: '/', labelKey: 'nav.dashboard', icon: HomeIcon },
    {
      path: '/projects',
      labelKey: 'nav.projects',
      icon: FolderIcon,
      submodules: [
        { path: '/projects', labelKey: 'nav.overview', icon: DashboardIcon },
        { path: '/posts', labelKey: 'nav.posts', icon: PostIcon },
        { path: '/flights', labelKey: 'nav.flights', icon: FlightIcon },
      ]
    },
    { path: '/media', labelKey: 'nav.media', icon: MediaIcon },
    { path: '/creator', labelKey: 'nav.creator', icon: CreatorIcon },
    { path: '/meta', labelKey: 'nav.meta', icon: MetaIcon },
    {
      path: '/marketing',
      labelKey: 'nav.marketing',
      icon: MarketingIcon,
      submodules: [
        { path: '/marketing', labelKey: 'nav.overview', icon: DashboardIcon },
        { path: '/marketing/calendar', labelKey: 'nav.calendar', icon: CalendarIcon },
        { path: '/marketing/pillars', labelKey: 'nav.pillars', icon: PillarIcon },
        { path: '/marketing/templates', labelKey: 'nav.templates', icon: TemplateIcon },
        { path: '/marketing/analytics', labelKey: 'nav.analytics', icon: AnalyticsIcon },
      ]
    },
    { path: '/tenants', labelKey: 'nav.tenants', icon: BuildingIcon },
    ...(user?.role === 'admin' ? [{
      path: '/admin',
      labelKey: 'nav.admin',
      icon: CogIcon,
      submodules: [
        { path: '/admin', labelKey: 'nav.users', icon: UserIcon },
        { path: '/admin/tenants', labelKey: 'nav.tenants', icon: BuildingIcon },
      ]
    }] : []),
  ];

  // Get current parent module (also checks submodule paths)
  const currentModule = navLinks.find(link => {
    if (link.path === '/') return location.pathname === '/';
    // Check if current path matches the main module path
    if (location.pathname.startsWith(link.path)) return true;
    // Check if current path matches any submodule path
    if (link.submodules?.some(sub =>
      sub.path === '/' ? location.pathname === '/' : location.pathname.startsWith(sub.path)
    )) return true;
    return false;
  });
  const hasSubmodules = currentModule?.submodules && currentModule.submodules.length > 0;

  // Get initials for avatar
  const getInitials = () => {
    if (user?.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }
    return user?.email?.[0]?.toUpperCase() || '?';
  };

  // Get first name for display
  const getFirstName = () => {
    return user?.name?.split(' ')[0] || user?.email;
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Staging Banner - sticky at top */}
      {isStaging && (
        <div className="sticky top-0 z-50 text-center text-xs py-1 font-medium" style={{ backgroundColor: '#DDD19C', color: '#5C5630' }}>
          {t('stagingBanner')}
        </div>
      )}

      {/* Sticky Header */}
      <header className={`sticky ${isStaging ? 'top-6' : 'top-0'} z-40 bg-white shadow-sm ${isStaging ? 'border-t-2' : ''}`} style={isStaging ? { borderTopColor: '#DDD19C' } : {}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Left: Logo & Navigation Icons */}
            <div className="flex items-center">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img src="/logo.svg" alt={`${t('brandDein')} ${t('brandMarketingHub')}`} className="h-9 w-9 rounded-lg mr-2" />
                <div className="hidden sm:flex flex-col leading-none">
                  <span className="text-sm font-medium text-gray-500">{t('brandDein')}</span>
                  <span className="text-sm font-bold text-gray-900">{t('brandMarketingHub')}</span>
                </div>
              </Link>

              {/* Divider */}
              <div className="hidden md:block ml-4 h-8 w-px bg-gray-200" />

              {/* Navigation Dropdown (Desktop) - always visible */}
              <div className="hidden md:block relative ml-3" ref={navMenuDesktopRef}>
                <button
                  onClick={() => setIsNavMenuOpen(!isNavMenuOpen)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    isNavMenuOpen
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                  }`}
                >
                  <GridIcon className="h-5 w-5" />
                  <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${isNavMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Navigation Dropdown Menu */}
                {isNavMenuOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-2 z-50">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                          isActive(link.path, link.submodules)
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => setIsNavMenuOpen(false)}
                      >
                        <link.icon className={`h-5 w-5 flex-shrink-0 ${isActive(link.path, link.submodules) ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className="font-medium">{t(link.labelKey)}</span>
                        {isActive(link.path, link.submodules) && (
                          <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-600" />
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Navigation Icon Bar (Desktop) - show all when no submodules, show only active when submodules */}
              <nav className="hidden md:flex items-center ml-2 gap-0.5">
                {hasSubmodules ? (
                  // Show only the active module when it has submodules
                  currentModule && (
                    <Link
                      to={currentModule.path}
                      className="flex flex-col items-center px-2.5 py-1.5 rounded-lg transition-colors min-w-[52px] bg-blue-50 text-blue-700"
                      title={t(currentModule.labelKey)}
                    >
                      <currentModule.icon className="h-5 w-5 text-blue-600" />
                      {showNavLabels && (
                        <span className="text-[10px] mt-0.5 font-medium leading-tight truncate max-w-[48px]">
                          {t(currentModule.labelKey)}
                        </span>
                      )}
                    </Link>
                  )
                ) : (
                  // Show all modules when no submodules
                  navLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`flex flex-col items-center px-2.5 py-1.5 rounded-lg transition-colors min-w-[52px] ${
                        isActive(link.path, link.submodules)
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                      }`}
                      title={t(link.labelKey)}
                    >
                      <link.icon className={`h-5 w-5 ${isActive(link.path, link.submodules) ? 'text-blue-600' : ''}`} />
                      {showNavLabels && (
                        <span className="text-[10px] mt-0.5 font-medium leading-tight truncate max-w-[48px]">
                          {t(link.labelKey)}
                        </span>
                      )}
                    </Link>
                  ))
                )}
              </nav>

              {/* Submodule Navigation (Desktop) - shown after active module */}
              {hasSubmodules && (
                <div className="hidden md:flex items-center ml-2">
                  <div className="h-6 w-px bg-gray-200 mr-2" />
                  <div className="flex items-center bg-gray-50 rounded-lg p-1 gap-0.5">
                    {currentModule?.submodules?.map((sub) => {
                      const isSubActive = location.pathname === sub.path;
                      return (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          className={`flex flex-col items-center px-2.5 py-1 rounded-md transition-colors min-w-[50px] ${
                            isSubActive
                              ? 'bg-white text-blue-700 shadow-sm'
                              : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'
                          }`}
                          title={t(sub.labelKey)}
                        >
                          <sub.icon className={`h-4 w-4 ${isSubActive ? 'text-blue-600' : ''}`} />
                          {showNavLabels && (
                            <span className="text-[9px] mt-0.5 font-medium leading-tight truncate max-w-[44px]">
                              {t(sub.labelKey)}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Mobile Navigation Dropdown */}
              <div className="relative ml-3 md:hidden" ref={navMenuRef}>
                <button
                  onClick={() => setIsNavMenuOpen(!isNavMenuOpen)}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isNavMenuOpen
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <GridIcon className="h-5 w-5" />
                  <ChevronDownIcon className={`h-4 w-4 transition-transform ${isNavMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Mobile Dropdown Menu */}
                {isNavMenuOpen && (
                  <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-2 z-50">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                          isActive(link.path, link.submodules)
                            ? 'bg-blue-50 text-blue-700'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        onClick={() => setIsNavMenuOpen(false)}
                      >
                        <link.icon className={`h-5 w-5 flex-shrink-0 ${isActive(link.path, link.submodules) ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className="font-medium">{t(link.labelKey)}</span>
                        {isActive(link.path, link.submodules) && (
                          <span className="ml-auto w-1.5 h-1.5 rounded-full bg-blue-600" />
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Profile */}
            <div className="flex items-center">
              {/* User Display ID (Desktop) */}
              {user?.display_id && (
                <span className="hidden lg:inline-block text-xs text-gray-400 mr-3 font-mono">
                  {user.display_id}
                </span>
              )}

              {/* Profile Dropdown */}
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                  className="flex items-center space-x-2 p-1 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  {/* Avatar */}
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.name || user.email}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center">
                      <span className="text-white text-sm font-medium">{getInitials()}</span>
                    </div>
                  )}
                  {/* Name (Desktop) */}
                  <span className="hidden md:block text-sm font-medium text-gray-700 max-w-32 truncate">
                    {getFirstName()}
                  </span>
                  <ChevronDownIcon className="hidden md:block h-4 w-4 text-gray-400" />
                </button>

                {/* Dropdown Menu */}
                {isProfileMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
                    <div className="py-1">
                      {/* User Info */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user?.name || t('noName')}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                        <p className="text-xs text-gray-400 font-mono mt-1">{user?.display_id}</p>
                      </div>

                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        <UserIcon className="inline-block h-4 w-4 mr-2 text-gray-400" />
                        {t('myProfile')}
                      </Link>

                      <Link
                        to="/trash"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        <TrashIcon className="inline-block h-4 w-4 mr-2 text-gray-400" />
                        {t('trash')}
                      </Link>

                      {/* Nav Labels Toggle (Desktop only) */}
                      <button
                        onClick={toggleNavLabels}
                        className="hidden md:flex w-full items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        <span>
                          <TagIcon className="inline-block h-4 w-4 mr-2 text-gray-400" />
                          {t('navLabels')}
                        </span>
                        <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${showNavLabels ? 'bg-blue-600' : 'bg-gray-200'}`}>
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showNavLabels ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </span>
                      </button>

                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <LogoutIcon className="inline-block h-4 w-4 mr-2" />
                          {t('logout')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </header>

      {/* Main Content */}
      <main className="flex-1 pb-8">{children}</main>

      {/* Sticky Footer with Version and Legal Links */}
      <footer className="sticky bottom-0 py-3 text-center text-xs text-gray-400 bg-gray-100 border-t border-gray-200">
        <span>{t('brandDein')} {t('brandMarketingHub')} v{APP_VERSION}</span>
        <span className="mx-2">·</span>
        <Link to="/impressum" className="hover:text-gray-600">{t('imprint')}</Link>
        <span className="mx-2">·</span>
        <Link to="/datenschutz" className="hover:text-gray-600">{t('privacy')}</Link>
        {isStaging && (
          <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: '#DDD19C', color: '#5C5630' }}>
            STAGING
          </span>
        )}
      </footer>
    </div>
  );
}

// App Version from vite.config.ts (reads from git tag)
declare const __APP_VERSION__: string;
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '1.0.0';

// Icons
function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

function FolderIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
    </svg>
  );
}

function CogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  );
}

function MediaIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function CreatorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
    </svg>
  );
}

function FlightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function PostIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  );
}

function TrashIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function MarketingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function MetaIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

function GridIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
  );
}

// Submodule Icons
function DashboardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  );
}

function PillarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  );
}

function AnalyticsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}
